import { useEffect } from "react";
import { publicApi } from "../lib/api";

/**
 * AnalyticsInjector
 * -----------------
 * Reads the public `site_config` once at mount and injects the tracking
 * scripts for whichever IDs are configured. Nothing is injected if the
 * corresponding ID is missing — this keeps the UI editable via the admin
 * panel without any code change.
 *
 * Supported providers (all IDs live in the `site_settings` table):
 *   - Google Analytics 4  (site_settings.ga4_id       — e.g. "G-XXXXXXXXXX")
 *   - Google Tag Manager  (site_settings.gtm_id       — e.g. "GTM-XXXXXXX")
 *   - Meta Pixel          (site_settings.meta_pixel_id — numeric string)
 *   - Microsoft Clarity   (site_settings.clarity_id    — e.g. "abcd1234")
 *
 * Design notes:
 *   - Scripts are appended to <head>; we tag every injected node with a
 *     custom `data-injected-by="analytics-injector"` attribute so the same
 *     component can safely no-op on hot-reload / route changes.
 *   - We never block rendering — every fetch failure results in a silent
 *     skip (tracking is optional; the site must render regardless).
 *   - No PII is sent; all trackers respect the standard cookie consent flow
 *     the browser applies at the provider level.
 */
const MARK = "analytics-injector";

function alreadyInjected(id) {
  return !!document.querySelector(`[data-injected-by="${MARK}"][data-provider="${id}"]`);
}

function injectScript({ provider, src, inner, async = true }) {
  if (alreadyInjected(provider)) return;
  const s = document.createElement("script");
  s.setAttribute("data-injected-by", MARK);
  s.setAttribute("data-provider", provider);
  if (async) s.async = true;
  if (src) s.src = src;
  if (inner) s.text = inner;
  document.head.appendChild(s);
}

function injectGA4(id) {
  if (!id) return;
  injectScript({ provider: `ga4-lib-${id}`, src: `https://www.googletagmanager.com/gtag/js?id=${id}` });
  injectScript({
    provider: `ga4-init-${id}`,
    inner: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${id}');`,
  });
}

function injectGTM(id) {
  if (!id) return;
  injectScript({
    provider: `gtm-${id}`,
    inner: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`,
  });
  // <noscript> iframe fallback for GTM
  if (!document.querySelector(`iframe[src*="${id}"]`)) {
    const noscript = document.createElement("noscript");
    noscript.setAttribute("data-injected-by", MARK);
    noscript.setAttribute("data-provider", `gtm-noscript-${id}`);
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.appendChild(noscript);
  }
}

function injectMetaPixel(id) {
  if (!id) return;
  injectScript({
    provider: `meta-pixel-${id}`,
    inner: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`,
  });
}

function injectClarity(id) {
  if (!id) return;
  injectScript({
    provider: `clarity-${id}`,
    inner: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${id}");`,
  });
}

export function AnalyticsInjector() {
  useEffect(() => {
    let cancelled = false;
    publicApi
      .getSiteConfig()
      .then((r) => {
        if (cancelled) return;
        const cfg = r?.data || {};
        injectGA4(cfg.ga4_id);
        injectGTM(cfg.gtm_id);
        injectMetaPixel(cfg.meta_pixel_id);
        injectClarity(cfg.clarity_id);
      })
      .catch(() => { /* silent: tracking is best-effort */ });
    return () => { cancelled = true; };
  }, []);
  return null;
}

export default AnalyticsInjector;
